import React, { useRef, memo, useState } from 'react';
import { css } from '@emotion/react';
import {
  color,
  Icon,
  Modal,
  shadow,
  spacing,
  typography
} from '@uniquegood/realworld-studio-design';

import { useHistory, useParams } from 'react-router-dom';
import { coreApi } from '@/api';
import { onMobile } from '@/styles/responsive';
import {
  DraggableMissionList,
  DraggableQuestList,
  MissionCard
} from '@/pages/missions/components/missionList';
import MissionListContextMenu from '@/pages/missions/components/missionList/MissionListContextMenu';
import SquareButton from '@/components/SquareButton';

import { MissionViewType } from '@/pages/missions/types';
import { useIsMobile, useModalState, usePromotionModal } from '@/hooks';
import useMission from '@/hooks/useMission';
import { createNewMission, duplicateMission, MISSION_API_VERSION_HEADER } from '../../utils';
import { ClickEventName, PromotionTriggerLocation, track } from '@/track';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { toast } from '@/utils';

interface CardListProps {
  missionView: MissionViewType;
  root: HTMLElement | null;
}

interface MissionContextMenuState {
  x: number;
  y: number;
  mission: Mission;
  index: number;
}

const CardList = ({ root, missionView }: CardListProps) => {
  const { appId, projectId, scenarioId, missionId, questId } = useParams<AppParam>();

  const { modal, openModal, closeModal } = useModalState();
  const history = useHistory();

  const isMobile = useIsMobile();
  const { missions, mutate } = useMission();
  const [duplicatingMissionId, setDuplicatingMissionId] = useState<number | null>(null);
  const [deletingMissionId, setDeletingMissionId] = useState<number | null>(null);
  const [contextMenu, setContextMenu] = useState<MissionContextMenuState | null>(null);

  const createMissionPage = `/apps/${appId}/projects/${projectId}/scenarios/${scenarioId}/missions/new`;
  const parentPath = `/apps/${appId}/projects/${projectId}/scenarios/${scenarioId}`;
  const isCreateNewMission = history.location.pathname === createMissionPage;

  const openChangeMissionReOrderModal = () => {
    openModal({
      size: 'medium',
      title: '미션 순서 변경',
      children: <DraggableMissionList missions={missions} />,
      secondaryAction: { content: '닫기', onAction: closeModal }
    });
  };

  const itemsRef = useRef<HTMLElement[]>([]);

  async function onCreateMission() {
    const currentIndex =
      missions?.findIndex((mission) => mission.id.toString() === missionId) ?? -1;
    const order = currentIndex >= 0 ? currentIndex + 1 : undefined;

    const mission = await createNewMission({ appId, scenarioId, order });
    track.onClick({
      clickEventName: ClickEventName.click_editgame_mission_new_mission
    });
    await mutate();
    history.push(
      `/apps/${appId}/projects/${projectId}/scenarios/${scenarioId}/missions/${mission.id}`
    );
  }

  async function onDuplicateMission(mission: Mission, index: number) {
    if (duplicatingMissionId) return;

    setDuplicatingMissionId(mission.id);

    try {
      const copiedMission = await duplicateMission({
        appId,
        scenarioId,
        mission,
        order: index + 1
      });

      await mutate();
      history.push(
        `/apps/${appId}/projects/${projectId}/scenarios/${scenarioId}/missions/${copiedMission.id}`
      );
      toast({ message: '미션을 복제했어요' });
    } finally {
      setDuplicatingMissionId(null);
    }
  }

  function openMissionContextMenu(
    event: React.MouseEvent,
    mission: Mission,
    index: number
  ) {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      mission,
      index
    });
  }

  function onDeleteMission(mission: Mission, index: number) {
    openModal({
      title: '미션을 삭제하시겠어요?',
      size: 'small',
      children: '삭제한 미션은 다시 복구할 수 없어요',
      primaryAction: {
        content: '삭제',
        icon: 'trash_alt_solid',
        type: 'destructive',
        loading: deletingMissionId === mission.id,
        disabled: deletingMissionId === mission.id,
        onAction: async () => {
          setDeletingMissionId(mission.id);

          try {
            await coreApi.delete(
              `/apps/${appId}/missions/${mission.id}`,
              MISSION_API_VERSION_HEADER
            );
            await mutate();

            const isCurrentMissionDeleted =
              mission.id.toString() === missionId ||
              mission.quests?.some((quest) => quest.id.toString() === questId);
            const fallbackMission = missions?.[index - 1] ?? missions?.[index + 1];

            if (isCurrentMissionDeleted) {
              history.push(
                fallbackMission ? `${parentPath}/missions/${fallbackMission.id}` : parentPath
              );
            }
            toast({ message: '미션을 삭제했어요' });
          } finally {
            setDeletingMissionId(null);
            closeModal();
          }
        }
      },
      secondaryAction: {
        content: '취소',
        onAction: closeModal
      }
    });
  }

  const { plan } = usePlanLimits();
  const { openPromotionModal } = usePromotionModal();

  const handlePlanDetailClick = () => {
    openPromotionModal({ triggerLocation: PromotionTriggerLocation.mission_edit_banner });
  };

  const modalPrimaryAction = modal.primaryAction
    ? {
        ...modal.primaryAction,
        loading: deletingMissionId !== null,
        disabled: deletingMissionId !== null
      }
    : undefined;

  return (
    <div css={MissionDirection}>
      <Modal {...modal} primaryAction={modalPrimaryAction} />

      {/* 요금제 업그레이드 배너 */}
      {plan === 'Free' && (
        <div css={UpgradeBannerStyle}>
          <div css={UpgradeBannerContentStyle}>
            <div>
              <Icon icon="jewel_solid" size="20px" color="#FFFFFF" />
            </div>
            <p css={UpgradeBannerTitleStyle}>
              더 몰입감 있는
              <br />
              콘텐츠를 만들고 싶다면?
            </p>
            <p css={UpgradeBannerDescStyle}>
              GPS, 카메라 활용 실감 기술과
              <br />
              넉넉한 AI 크레딧 제공까지!
            </p>
          </div>
          <button type="button" css={UpgradeBannerButtonStyle} onClick={handlePlanDetailClick}>
            요금제 자세히 보기
          </button>
        </div>
      )}
      {missions &&
        missions.length > 0 &&
        missions.map((mission, index) => {
          const isSelected = questId ? false : missionId === mission.id.toString();
          const { quests } = mission;

          const onClickMissionCard = () => {
            if (root && itemsRef.current.length > index) {
              const { vertical, horizontal } = smoothScroll(root, itemsRef.current[index]);

              if (isMobile) {
                horizontal();
              } else {
                vertical();
              }
            }
          };

          const setTargetRef = (el: HTMLDivElement | null) => {
            if (el) {
              itemsRef.current[index] = el;
            }
          };

          return (
            <div key={mission.id} css={MissionInfoCardStyle} ref={(el) => setTargetRef(el)}>
              <div
                onContextMenu={(event) => openMissionContextMenu(event, mission, index)}
              >
                <MissionCard
                  onClick={onClickMissionCard}
                  hasImage={missionView === 'CardView'}
                  mission={mission}
                  isSelected={isSelected}
                />
              </div>
              <DraggableQuestList quests={quests} mission={mission} boundaryElement={root} />
            </div>
          );
        })}
      {contextMenu && (
        <MissionListContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          boundaryElement={root}
          onClose={() => setContextMenu(null)}
          items={[
            {
              label: '미션 복제',
              icon: 'copy_solid',
              disabled: duplicatingMissionId === contextMenu.mission.id,
              onClick: () => onDuplicateMission(contextMenu.mission, contextMenu.index)
            },
            {
              label: '미션 삭제',
              icon: 'trash_alt_solid',
              destructive: true,
              disabled: deletingMissionId === contextMenu.mission.id,
              onClick: () => onDeleteMission(contextMenu.mission, contextMenu.index)
            }
          ]}
        />
      )}
      <div css={SquareButtonGroupLayout}>
        <SquareButton icon="plus_solid" onClick={onCreateMission} disabled={isCreateNewMission}>
          새 미션
        </SquareButton>
        <SquareButton
          icon="exchange_alt_solid"
          onClick={openChangeMissionReOrderModal}
          disabled={!!missions && missions.length === 0}
        >
          순서 변경
        </SquareButton>
      </div>
    </div>
  );
};

function smoothScroll(scrollContainer: HTMLElement, { offsetTop, offsetLeft }: HTMLElement) {
  function vertical() {
    const headerHeight = 58;
    const gap = 24;

    const offset = headerHeight + gap;

    scrollContainer.scrollTo({
      top: offsetTop - offset,
      behavior: 'smooth'
    });
  }

  function horizontal() {
    const itemPadding = 16;
    const gap = 8;

    const offset = itemPadding + gap;

    scrollContainer.scrollTo({
      left: offsetLeft - offset,
      behavior: 'smooth'
    });
  }

  return { vertical, horizontal };
}

export default memo(CardList);

const MissionDirection = css`
  width: 192px;
  margin-bottom: ${spacing.common.xsmall};

  ${onMobile} {
    display: flex;
    margin-bottom: 0px;
    width: initial;
  }
`;

const MissionInfoCardStyle = css`
  position: relative;
  background-color: ${color.palette_gray_tint_01};
  ${shadow.popup};
  border-radius: ${spacing.common.medium};
  padding: ${spacing.common.medium};
  display: flex;
  flex-direction: column;
  margin-top: ${spacing.common.medium};
  cursor: pointer;
  &:first-of-type {
    margin-top: 0px;
  }

  ${onMobile} {
    flex-direction: row;
    margin-top: 0px;
    margin-right: ${spacing.common.small};

    &:last-of-type {
      margin-right: 0px;
    }
  }
`;

const SquareButtonGroupLayout = css`
  display: flex;
  margin-top: ${spacing.common.medium};

  & > button:last-of-type {
    margin-left: ${spacing.common.small};
  }

  ${onMobile} {
    flex-direction: column;
    justify-content: center;
    margin: 0 ${spacing.common.medium} 0 ${spacing.common.small};

    & > button:last-of-type {
      margin-top: ${spacing.common.small};
      margin-left: 0px !important;
    }
  }
`;

const UpgradeBannerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 16px;
  background: linear-gradient(90deg, #7800ff 0%, #829bff 100%);
  width: 100%;
  margin-bottom: ${spacing.common.medium};

  ${onMobile} {
    display: none;
  }
`;

const UpgradeBannerContentStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
`;

const UpgradeBannerTitleStyle = css`
  font-family: ${typography.family.primary};
  font-size: 14px;
  font-weight: ${typography.weight.bold};
  line-height: 20px;
  color: #ffffff;
  margin: 0;
`;

const UpgradeBannerDescStyle = css`
  font-family: ${typography.family.primary};
  font-size: 12px;
  font-weight: ${typography.weight.regular};
  line-height: 16px;
  color: #ffffff;
  margin: 0;
`;

const UpgradeBannerButtonStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 8px 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  cursor: pointer;
  font-family: ${typography.family.primary};
  font-size: 14px;
  font-weight: ${typography.weight.medium};
  line-height: 20px;
  color: #ffffff;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;
